'use client'

import { useSyncExternalStore } from 'react'

export type WizardNavigationState = {
  isNavigating: boolean
  targetHref: string | null
  startedAt: number | null
  showInterstitial: boolean
}

type WizardRouter = {
  push: (href: string) => void
  replace: (href: string) => void
  prefetch?: (href: string) => Promise<void> | void
}

const NAVIGATION_TIMEOUT_MS = 8000
const INTERSTITIAL_DELAY_MS = 120

const defaultState: WizardNavigationState = {
  isNavigating: false,
  targetHref: null,
  startedAt: null,
  showInterstitial: false,
}

let state: WizardNavigationState = defaultState
let timeoutRef: ReturnType<typeof setTimeout> | null = null
let interstitialDelayRef: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function emitChange() {
  for (const listener of listeners) {
    listener()
  }
}

function clearNavigationTimeout() {
  if (!timeoutRef) return
  clearTimeout(timeoutRef)
  timeoutRef = null
}

function clearInterstitialDelay() {
  if (!interstitialDelayRef) return
  clearTimeout(interstitialDelayRef)
  interstitialDelayRef = null
}

function setState(nextState: WizardNavigationState) {
  state = nextState
  emitChange()
}

function shouldUseViewTransitions(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof document === 'undefined') return false

  const canUseViewTransition =
    typeof document.startViewTransition === 'function'
  const prefersReducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

  return canUseViewTransition && !prefersReducedMotion
}

export function getWizardNavigationState(): WizardNavigationState {
  return state
}

export function startWizardNavigation(
  targetHref: string,
  timeoutMs = NAVIGATION_TIMEOUT_MS,
  interstitialDelayMs = INTERSTITIAL_DELAY_MS,
) {
  clearNavigationTimeout()
  clearInterstitialDelay()
  setState({
    isNavigating: true,
    targetHref,
    startedAt: Date.now(),
    showInterstitial: false,
  })

  interstitialDelayRef = setTimeout(() => {
    if (!state.isNavigating || state.targetHref !== targetHref) return
    setState({
      ...state,
      showInterstitial: true,
    })
  }, interstitialDelayMs)

  timeoutRef = setTimeout(() => {
    completeWizardNavigation()
  }, timeoutMs)
}

export function completeWizardNavigation() {
  clearNavigationTimeout()
  clearInterstitialDelay()
  if (
    !state.isNavigating &&
    state.targetHref === null &&
    state.startedAt === null &&
    !state.showInterstitial
  ) {
    return
  }
  setState(defaultState)
}

export function subscribeWizardNavigationState(
  callback: () => void,
): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

export async function navigateWizardRoute({
  router,
  href,
  replace = false,
  prefetch = true,
}: {
  router: WizardRouter
  href: string
  replace?: boolean
  prefetch?: boolean
}) {
  if (state.isNavigating && state.targetHref === href) {
    return
  }

  startWizardNavigation(href)

  if (prefetch && typeof router.prefetch === 'function') {
    try {
      void Promise.resolve(router.prefetch(href)).catch(() => {
        // Prefetch failure should never block navigation.
      })
    } catch {
      // Prefetch failure should never block navigation.
    }
  }

  const navigate = () => {
    if (replace) {
      router.replace(href)
      return
    }
    router.push(href)
  }

  if (shouldUseViewTransitions()) {
    document.startViewTransition(() => {
      navigate()
    })
    return
  }

  navigate()
}

export function useWizardNavigationState(): WizardNavigationState {
  return useSyncExternalStore(
    subscribeWizardNavigationState,
    getWizardNavigationState,
    getWizardNavigationState,
  )
}
